import { z } from "zod";

// Clevis Relay — good work, passed along. Every customer gets a link; a
// friend who books through it is a referral; when the friend's job is paid
// (or done — the company's choice) the referrer gets the company's reward:
// a gift card through the company's own Tremendous account, credit on their
// next job, or cash/check the office marks paid. Every rule here is the
// company's own (tenants/{t}/settings/referrals); nothing is fixed.
// Functions write everything; clients get realtime reads.

export const RewardKindSchema = z.enum(["gift_card", "credit", "cash"]);
export type RewardKind = z.infer<typeof RewardKindSchema>;

export const ReferralSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** What the referrer gets. */
  reward: z
    .object({
      kind: RewardKindSchema.default("gift_card"),
      /** A fixed amount, or a percent of the friend's first invoice (capped). */
      basis: z.enum(["fixed", "percent"]).default("fixed"),
      amountCents: z.number().int().nonnegative().max(100000000).default(5000),
      percentBps: z.number().int().nonnegative().max(10000).default(500),
      capCents: z.number().int().nonnegative().max(100000000).default(25000),
    })
    .default({}),
  /** What the new customer gets: a discount line on their first estimate. */
  friend: z
    .object({
      enabled: z.boolean().default(false),
      amountCents: z.number().int().nonnegative().max(100000000).default(2500),
    })
    .default({}),
  /** When the reward is earned. */
  payWhen: z.enum(["invoice_paid", "job_done"]).default("invoice_paid"),
  /** Who gets a link and a reward: homeowners (no company name) or every customer. */
  eligible: z.enum(["homeowners", "everyone"]).default("homeowners"),
  /** No reward on a first invoice under this. */
  minInvoiceCents: z.number().int().nonnegative().max(100000000).default(0),
  /** The landing page and the share text, in the company's words ({company}, {referrer}, {reward}, {friendOffer}, {link}). */
  headline: z.string().max(140).default(""),
  blurb: z.string().max(600).default(""),
  shareText: z.string().max(400).default(""),
  /** Put the customer's link in the "How did we do?" text after a job. */
  askAfterJob: z.boolean().default(true),
  /** The standing ask: a text a while after a paid job, repeated now and then. */
  ask: z
    .object({
      enabled: z.boolean().default(true),
      daysAfterPaid: z.number().int().min(1).max(365).default(14),
      everyMonths: z.number().int().min(1).max(36).default(6),
      homeownersOnly: z.boolean().default(true),
      /** Only customers with a paid job this recent. */
      paidWithinMonths: z.number().int().min(1).max(60).default(12),
      text: z.string().max(400).default(""),
    })
    .default({}),
  /** The company's own Tremendous account (the key lives in referralSecrets, never here). */
  tremendous: z
    .object({
      connected: z.boolean().default(false),
      summary: z.string().default(""),
      sandbox: z.boolean().default(false),
      fundingSourceId: z.string().default(""),
      /** Which gift cards the referrer can pick from (empty = whatever Tremendous offers). */
      productIds: z.array(z.string()).max(20).default([]),
      productLabel: z.string().max(120).default(""),
      connectedAt: z.number().nullable().default(null),
    })
    .default({}),
  updatedAt: z.number().optional(),
});
export type ReferralSettings = z.infer<typeof ReferralSettingsSchema>;

export const ReferralStatusSchema = z.enum(["referred", "booked", "done", "reward_due", "paid", "declined"]);
export type ReferralStatus = z.infer<typeof ReferralStatusSchema>;
export const REFERRAL_STATUS_LABEL: Record<ReferralStatus, string> = {
  referred: "Referred",
  booked: "Booked",
  done: "Job done",
  reward_due: "Reward due",
  paid: "Rewarded",
  declined: "Declined",
};

/** tenants/{t}/referrals/{id}: one friend sent by one customer. */
export const ReferralSchema = z.object({
  code: z.string(),
  referrerCustomerId: z.string(),
  referrerName: z.string().default(""),
  friendName: z.string().default(""),
  friendEmail: z.string().default(""),
  friendPhone: z.string().default(""),
  requestId: z.string().nullable().default(null),
  customerId: z.string().nullable().default(null),
  jobId: z.string().nullable().default(null),
  invoiceId: z.string().nullable().default(null),
  invoiceTotalCents: z.number().int().nonnegative().default(0),
  status: ReferralStatusSchema.default("referred"),
  rewardCents: z.number().int().nonnegative().default(0),
  rewardKind: RewardKindSchema.nullable().default(null),
  /** The discount promised to the friend (a line on their first estimate). */
  friendDiscountCents: z.number().int().nonnegative().default(0),
  payout: z
    .object({
      method: z.enum(["tremendous", "credit", "cash"]),
      at: z.number(),
      byName: z.string().default(""),
      orderId: z.string().default(""),
      rewardId: z.string().default(""),
      /** The gift card link (Tremendous); texted/emailed to the referrer, kept here for the office. */
      link: z.string().default(""),
      note: z.string().default(""),
    })
    .nullable()
    .default(null),
  /** The expense the paid reward became (job profit, QuickBooks bill); none for credit. */
  expenseId: z.string().nullable().default(null),
  /** The campaign that asked the referrer (within 120 days of the ask). */
  campaignId: z.string().nullable().default(null),
  /** Why a Tremendous order didn't go through (the office can retry). */
  lastError: z.string().default(""),
  declineReason: z.string().default(""),
  createdAt: z.number(),
  bookedAt: z.number().nullable().default(null),
  doneAt: z.number().nullable().default(null),
  dueAt: z.number().nullable().default(null),
  paidAt: z.number().nullable().default(null),
  updatedAt: z.number(),
});
export type Referral = z.infer<typeof ReferralSchema>;

// ---- campaigns: "ask for referrals" sends, each with its own audience ----

/** Who a campaign reaches. Every knob is the company's. */
export const CampaignAudienceSchema = z.object({
  /** Customers with an invoice paid this recently; 0 = every customer. */
  paidWithinMonths: z.number().int().min(0).max(60).default(12),
  /** Only customers whose paid jobs were in these departments (empty = any). */
  departmentIds: z.array(z.string()).max(30).default([]),
  homeownersOnly: z.boolean().default(true),
  /** Skip anyone asked (by any campaign or the standing ask) this recently. */
  notAskedWithinDays: z.number().int().min(0).max(365).default(90),
});
export type CampaignAudience = z.infer<typeof CampaignAudienceSchema>;

export const CampaignStatusSchema = z.enum(["draft", "scheduled", "paused", "done"]);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

/** tenants/{t}/referralCampaigns/{id} */
export const ReferralCampaignSchema = z.object({
  name: z.string().min(1).max(80),
  channel: z.enum(["text", "email"]).default("text"),
  /** The message ({company}, {firstName}, {reward}, {link}); blank = the standard ask. */
  text: z.string().max(600).default(""),
  /** Email only. */
  subject: z.string().max(120).default(""),
  audience: CampaignAudienceSchema.default({}),
  schedule: z
    .object({
      kind: z.enum(["once", "repeat"]).default("once"),
      /** When (ms); null = as soon as it's scheduled. */
      at: z.number().nullable().default(null),
      everyMonths: z.number().int().min(1).max(36).default(6),
    })
    .default({}),
  status: CampaignStatusSchema.default("draft"),
  nextRunAt: z.number().nullable().default(null),
  lastRunAt: z.number().nullable().default(null),
  runs: z.array(z.object({ at: z.number(), queued: z.number().int(), skipped: z.number().int(), by: z.string().default("") })).max(24).default([]),
  totalQueued: z.number().int().default(0),
  createdByUid: z.string().default(""),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type ReferralCampaign = z.infer<typeof ReferralCampaignSchema>;

/** "Homeowners with a paid job in the last 12 months, not asked in 90 days" */
export function audienceLabel(a: CampaignAudience, departmentNames: string[] = []): string {
  const who = a.homeownersOnly ? "Homeowners" : "Customers";
  const paid = a.paidWithinMonths ? ` with a paid job in the last ${a.paidWithinMonths} month${a.paidWithinMonths === 1 ? "" : "s"}` : "";
  const dept = departmentNames.length ? ` in ${departmentNames.join(", ")}` : "";
  const asked = a.notAskedWithinDays ? `, not asked in ${a.notAskedWithinDays} days` : "";
  return `${who}${paid}${dept}${asked}`;
}

// ---- words, with the company's placeholders filled in ----

export const RELAY_TAGLINE = "Good work, passed along.";
export const DEFAULT_REFERRAL_HEADLINE = "{referrer} thinks you'll like {company}.";
export const DEFAULT_REFERRAL_BLURB = "Request service through this link and {referrer} gets {reward} from us once your job is done.{friendOffer}";
export const DEFAULT_SHARE_TEXT = "I use {company} and they do good work. If you need them, use my link: {link}";
export const DEFAULT_ASK_TEXT = "Thanks for choosing {company}! Know someone who needs us? Share your link and we'll send you {reward} when their job is done: {link}";
export const DEFAULT_REWARD_TEXT = "Thank you for sending {friend} our way! Here's your {reward} from {company}: {link}";
export const DEFAULT_REWARD_TEXT_NO_LINK = "Thank you for sending {friend} our way! Your {reward} from {company} is on its way.";

export function fillReferral(text: string, vars: Record<string, string>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? vars[k]! : m)).replace(/[ \t]{2,}/g, " ").trim();
}

const dollars = (c: number) => (c % 100 === 0 ? `$${c / 100}` : `$${(c / 100).toFixed(2)}`);

/** "a $50 Amazon gift card", "$50 credit on your next job", "$50", or "5% of their first invoice (up to $250)". */
export function rewardLabel(s: ReferralSettings): string {
  const r = s.reward;
  const amount = r.basis === "percent" ? `${(r.percentBps / 100).toString().replace(/\.0$/, "")}% of their first invoice${r.capCents ? ` (up to ${dollars(r.capCents)})` : ""}` : dollars(r.amountCents);
  if (r.kind === "credit") return `${amount} credit on your next job`;
  if (r.kind === "cash") return amount;
  const card = s.tremendous.productLabel?.trim() || "gift card";
  return r.basis === "percent" ? `a ${card} worth ${amount}` : `a ${amount} ${card}`;
}

/** " Your first job gets $25 off, too." or "". */
export function friendOfferLabel(s: ReferralSettings): string {
  return s.friend.enabled && s.friend.amountCents > 0 ? ` Your first job gets ${dollars(s.friend.amountCents)} off, too.` : "";
}

/** What the referrer earns on a first invoice of `invoiceCents` (0 when it's under the company's minimum). */
export function rewardCentsFor(s: ReferralSettings, invoiceCents: number): number {
  if (s.minInvoiceCents && invoiceCents < s.minInvoiceCents) return 0;
  if (s.reward.basis === "fixed") return s.reward.amountCents;
  const pct = Math.round((invoiceCents * s.reward.percentBps) / 10000);
  return s.reward.capCents ? Math.min(pct, s.reward.capCents) : pct;
}

/** A customer gets a link: homeowners only (no company name), or everyone. */
export function referralEligible(s: ReferralSettings, customer: { company?: string }): boolean {
  return s.eligible === "everyone" || !(customer.company ?? "").trim();
}

/** Readable, unambiguous codes (no 0/O, 1/I/L). */
export const REFERRAL_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
export const REFERRAL_CODE_LENGTH = 6;
export const referralCodeOk = (code: string) => new RegExp(`^[${REFERRAL_CODE_ALPHABET}]{${REFERRAL_CODE_LENGTH}}$`).test(code);
